// Home — TRIMIE Hi-Fi v1 (per docs/design-spec.md §4.4 + Hi-Fi PDF p.7)
//
// Layout (top → bottom):
//   1. Top row: TRIMIE wordmark · bell · avatar (avatar opens Profile)
//   2. Greeting: GOOD MORNING eyebrow + "What are you wearing today?" H1
//   3. "Start a try-on" primary purple card with Try on now → pill
//   4. Stats row: 3 tiles — In closet / Looks / Streak
//   5. Recent looks — horizontal scroll of recent try-ons

import { useEffect, useState } from 'react';
import api from './api';
import { resolveUrl } from './utils/media';

function timeOfDayGreeting() {
  const h = new Date().getHours();
  if (h < 5) return 'Up late';
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

export default function HomePage({ onNav, user, closetCount, tryonCount }) {
  const avatarUrl = resolveUrl(user?.profilePhotoUrl);
  const displayName =
    user?.displayName ||
    (user?.username && user.username.trim()) ||
    (user?.email ? user.email.split('@')[0] : 'you');

  const [recentLooks, setRecentLooks] = useState([]);

  useEffect(() => {
    api.get('/tryon/image/', { params: { status: 'completed' } })
      .then((res) => {
        const data = res.data?.data?.results ?? res.data?.data ?? [];
        const list = Array.isArray(data) ? data : [];
        setRecentLooks(list.filter(t => t.tryon_image_url).slice(0, 6));
      })
      .catch(() => setRecentLooks([]));
  }, []);

  return (
    <div className="screen home-screen-v2">
      {/* ── Top row ─────────────────────────────────────────────────────── */}
      <div className="home-v2-top">
        <span className="home-v2-wordmark">TRIMIE</span>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button className="btn-icon" aria-label="Notifications" type="button">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 9a6 6 0 0 1 12 0v5l2 3H4l2-3V9z" />
              <path d="M9 19a3 3 0 0 0 6 0" />
            </svg>
          </button>
          <button
            className="home-v2-avatar"
            onClick={() => onNav('me')}
            aria-label="Open profile"
            type="button"
          >
            {avatarUrl
              ? <img src={avatarUrl} alt="" />
              : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <circle cx="12" cy="9" r="3.5" />
                  <path d="M5 20c0-4 3.5-6 7-6s7 2 7 6" />
                </svg>
              )}
          </button>
        </div>
      </div>

      {/* ── Greeting ─────────────────────────────────────────────────────── */}
      <div className="home-v2-today">
        <span className="mono-eyebrow">{timeOfDayGreeting().toUpperCase()}, {displayName.toUpperCase()}</span>
        <h1 className="home-v2-greet">
          What are you wearing today?
        </h1>
      </div>

      {/* ── Start a try-on (primary purple card) ─────────────────────────── */}
      <button
        className="home-v2-tryon-card"
        onClick={() => onNav('tryon')}
        type="button"
      >
        <div className="home-v2-tryon-text">
          <div className="home-v2-tryon-title">Start a try-on</div>
          <div className="home-v2-tryon-sub">Snap a garment or paste a link</div>
          <span className="home-v2-tryon-cta">
            Try on now
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          </span>
        </div>
        <div className="home-v2-tryon-glyph" aria-hidden>
          <svg viewBox="0 0 80 100" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" preserveAspectRatio="xMidYMid meet">
            <circle cx="40" cy="18" r="9" />
            <path d="M22 92v-30c0-10 8-18 18-18s18 8 18 18v30" />
          </svg>
        </div>
      </button>

      {/* ── Stats row ────────────────────────────────────────────────────── */}
      <div className="home-v2-stats">
        <div className="home-v2-stat">
          <div className="home-v2-stat-num">{closetCount ?? 0}</div>
          <div className="home-v2-stat-label">In closet</div>
        </div>
        <div className="home-v2-stat">
          <div className="home-v2-stat-num">{tryonCount ?? 0}</div>
          <div className="home-v2-stat-label">Looks saved</div>
        </div>
        <div className="home-v2-stat">
          <div className="home-v2-stat-num">—</div>
          <div className="home-v2-stat-label">Streak</div>
        </div>
      </div>

      {/* ── Recent looks ─────────────────────────────────────────────────── */}
      {recentLooks.length > 0 && (
        <div className="home-v2-section">
          <div className="home-v2-section-head">
            <span className="home-v2-section-title">Recent looks</span>
            <button className="home-v2-section-link" onClick={() => onNav('looks')} type="button">
              See all →
            </button>
          </div>
          <div className="home-v2-hscroll">
            {recentLooks.map((item) => {
              const url = resolveUrl(item.tryon_image_url);
              return (
                <button
                  key={item.id}
                  className="recent-look-card"
                  onClick={() => onNav('looks')}
                  type="button"
                >
                  {url
                    ? <img src={url} alt="" />
                    : <div className="recent-look-empty" />}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div style={{ height: 16 }} />
    </div>
  );
}
